import React, { useEffect, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Descriptions,
  Drawer,
  Image,
  Input,
  message,
  Modal,
  Progress,
  Row,
  Space,
  Spin,
  Statistic,
  Steps,
  Tag,
  Timeline,
  Upload,
} from 'antd';
import {
  CheckOutlined,
  CloseOutlined,
  ExclamationCircleOutlined,
  PlayCircleOutlined,
  SendOutlined,
  UploadOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import type { UploadFile } from 'antd';
import { rectificationService } from '../services';
import {
  RECTIFICATION_SEVERITY_MAP,
  RECTIFICATION_SOURCE_MAP,
  RECTIFICATION_STATUS_MAP,
  CHECK_TYPE_MAP,
  RectificationStatus,
  type IRectification,
} from '../types';

interface RectificationDetailDrawerProps {
  open: boolean;
  rectificationId?: string;
  onClose: () => void;
  onSuccess?: () => void;
}

const RectificationDetailDrawer: React.FC<RectificationDetailDrawerProps> = ({
  open,
  rectificationId,
  onClose,
  onSuccess,
}) => {
  const [rectification, setRectification] = useState<IRectification | null>(null);
  const [loading, setLoading] = useState(false);
  const [actionModalVisible, setActionModalVisible] = useState(false);
  const [currentAction, setCurrentAction] = useState<string>('');
  const [comment, setComment] = useState('');
  const [rectificationAttachments, setRectificationAttachments] = useState<UploadFile[]>([]);
  const [historyEvents, setHistoryEvents] = useState<any[]>([]);

  const fetchRectification = async () => {
    if (!rectificationId) {
      setRectification(null);
      return;
    }

    try {
      setLoading(true);
      const [detailRes, historyRes] = await Promise.all([
        rectificationService.getById(rectificationId),
        rectificationService.getHistory(rectificationId),
      ]);
      if (detailRes.success && detailRes.data) {
        setRectification(detailRes.data);
      }
      if (historyRes.success && historyRes.data) {
        setHistoryEvents(historyRes.data);
      }
    } catch (error: any) {
      message.error(error.message || '获取整改详情失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && rectificationId) {
      fetchRectification();
      setComment('');
      setRectificationAttachments([]);
    }
  }, [open, rectificationId]);

  const openActionModal = (action: string) => {
    setCurrentAction(action);
    setActionModalVisible(true);
  };

  const handleAction = async () => {
    if (!rectificationId) return;

    try {
      let response;
      switch (currentAction) {
        case 'start':
          response = await rectificationService.start(rectificationId);
          break;
        case 'submit':
          response = await rectificationService.submit(rectificationId, {
            comment,
            rectificationAttachments: rectificationAttachments
              .filter(a => a.status === 'done' && a.response)
              .map(a => ({ url: (a.response as any)?.url || a.url || '', name: a.name || '' })),
          });
          break;
        case 'director_accept':
          response = await rectificationService.directorAccept(rectificationId, { accepted: true, comment });
          break;
        case 'director_reject':
          response = await rectificationService.directorAccept(rectificationId, { accepted: false, comment });
          break;
        case 'owner_accept':
          response = await rectificationService.ownerAccept(rectificationId, { accepted: true, comment });
          break;
        case 'owner_reject':
          response = await rectificationService.ownerAccept(rectificationId, { accepted: false, comment });
          break;
      }

      if (response?.success) {
        message.success('操作成功');
        setActionModalVisible(false);
        setComment('');
        setRectificationAttachments([]);
        await fetchRectification();
        onSuccess?.();
      }
    } catch (error: any) {
      message.error(error.message || '操作失败');
    }
  };

  const renderActionButtons = () => {
    if (!rectification) return null;

    switch (rectification.status) {
      case RectificationStatus.PENDING:
      case RectificationStatus.NOTIFIED:
        return <Button type="primary" icon={<PlayCircleOutlined />} onClick={() => openActionModal('start')}>开始处理</Button>;
      case RectificationStatus.IN_PROGRESS:
        return <Button type="primary" icon={<SendOutlined />} onClick={() => openActionModal('submit')}>提交整改</Button>;
      case RectificationStatus.SUBMITTED:
        return (
          <Space>
            <Button type="primary" icon={<CheckOutlined />} onClick={() => openActionModal('director_accept')}>总监验收通过</Button>
            <Button danger icon={<CloseOutlined />} onClick={() => openActionModal('director_reject')}>总监验收不通过</Button>
          </Space>
        );
      case RectificationStatus.DIRECTOR_ACCEPTED:
        return (
          <Space>
            <Button type="primary" icon={<CheckOutlined />} onClick={() => openActionModal('owner_accept')}>业主验收通过</Button>
            <Button danger icon={<CloseOutlined />} onClick={() => openActionModal('owner_reject')}>业主验收不通过</Button>
          </Space>
        );
      default:
        return null;
    }
  };

  const statusInfo = rectification ? RECTIFICATION_STATUS_MAP[rectification.status] : undefined;
  const severityInfo = rectification ? RECTIFICATION_SEVERITY_MAP[rectification.severity] : undefined;

  const currentStep = (() => {
    switch (rectification?.status) {
      case RectificationStatus.PENDING: return 0;
      case RectificationStatus.NOTIFIED: return 0;
      case RectificationStatus.IN_PROGRESS: return 1;
      case RectificationStatus.SUBMITTED: return 2;
      case RectificationStatus.DIRECTOR_ACCEPTED: return 3;
      case RectificationStatus.OWNER_ACCEPTED:
      case RectificationStatus.CLOSED: return 4;
      default: return 0;
    }
  })();

  const stepItems = [
    { title: '发起', description: rectification?.createdAt ? new Date(rectification.createdAt).toLocaleDateString() : '' },
    { title: '整改中', description: '' },
    { title: '提交', description: '' },
    { title: '总监验收', description: '' },
    { title: '甲方验收', description: '' },
  ];

  const actualCost = rectification?.actualCost;
  const estimatedTotal = ((rectification?.reworkLaborCost || 0) +
    (rectification?.reworkMaterialCost || 0) +
    (rectification?.reworkEquipmentCost || 0) +
    (rectification?.reworkSubcontractCost || 0) +
    (rectification?.coordinationExtraCost || 0) +
    (rectification?.delayImpactCost || 0)) || 0;

  return (
    <>
      <Drawer
        title={rectification ? `整改详情 - ${rectification.rectificationNo}` : '整改详情'}
        open={open}
        onClose={onClose}
        width={980}
        destroyOnClose
        extra={renderActionButtons()}
      >
        {loading ? (
          <div style={{ padding: 48, textAlign: 'center' }}><Spin size="large" /></div>
        ) : !rectification ? (
          <div style={{ padding: 24, textAlign: 'center' }}>整改不存在</div>
        ) : (
          <>
            {/* 状态步骤条 */}
            <Card size="small" style={{ marginBottom: 16 }}>
              <Steps
                current={currentStep}
                size="small"
                items={stepItems.map((step, i) => ({
                  ...step,
                  status: i < currentStep ? 'finish' : i === currentStep ? 'process' : 'wait',
                }))}
              />
            </Card>

            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={4}>
                <Statistic
                  title="当前进度"
                  value={rectification.progress ?? 0}
                  suffix="%"
                  prefix={<PlayCircleOutlined />}
                  valueStyle={{ color: (rectification.progress ?? 0) > 0 ? '#1890ff' : '#999' }}
                />
              </Col>
              <Col span={4}>
                <Statistic
                  title="打回次数"
                  value={rectification.rejectCount ?? 0}
                  prefix={(rectification.rejectCount ?? 0) > 0 ? <WarningOutlined style={{ color: 'red' }} /> : <CheckOutlined style={{ color: '#52c41a' }} />}
                  valueStyle={{ color: (rectification.rejectCount ?? 0) > 0 ? 'red' : '#52c41a' }}
                />
              </Col>
              <Col span={4}>
                <Statistic
                  title="预计成本"
                  value={estimatedTotal}
                  precision={2}
                  prefix="¥"
                  valueStyle={{ color: '#faad14' }}
                />
              </Col>
              <Col span={4}>
                <Statistic
                  title="实际成本"
                  value={actualCost?.total ?? 0}
                  precision={2}
                  prefix="¥"
                  valueStyle={{ color: actualCost?.total ? '#52c41a' : '#999' }}
                />
              </Col>
              <Col span={4}>
                <Statistic
                  title="扣除金额"
                  value={rectification.deductedAmount ?? 0}
                  precision={2}
                  prefix="¥"
                  valueStyle={{ color: 'red' }}
                />
              </Col>
            </Row>

            {/* 进度条（如果有整改进度） */}
            {rectification.status === RectificationStatus.IN_PROGRESS && (
              <Card size="small" style={{ marginBottom: 16 }}>
                <Space>
                  <span>整改进度：</span>
                  <Progress percent={rectification.progress ?? 0} size="small" style={{ width: 300 }} />
                </Space>
              </Card>
            )}

            {/* 基本信息 */}
            <Card bordered={false} bodyStyle={{ padding: 0 }} style={{ marginBottom: 16 }}>
              <Descriptions bordered column={2} size="small">
                <Descriptions.Item label="整改编号">{rectification.rectificationNo}</Descriptions.Item>
                <Descriptions.Item label="整改标题">{rectification.title}</Descriptions.Item>
                <Descriptions.Item label="状态"><Tag color={statusInfo?.color}>{statusInfo?.label}</Tag></Descriptions.Item>
                <Descriptions.Item label="严重程度">
                  <Tag color={severityInfo?.color}>{severityInfo?.label}</Tag>
                  {rectification.deductionPercentage > 0 && <span style={{ marginLeft: 8, color: '#999' }}>扣除{rectification.deductionPercentage}%</span>}
                </Descriptions.Item>
                <Descriptions.Item label="问题来源">
                  {RECTIFICATION_SOURCE_MAP[rectification.source]}
                  {rectification.checkType && (
                    <Tag color="blue" style={{ marginLeft: 8 }}>{CHECK_TYPE_MAP[rectification.checkType]}</Tag>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="责任人">
                  {rectification.responsibleUserName || '-'}
                  {rectification.responsibleTeam && <span style={{ color: '#999', marginLeft: 4 }}>（{rectification.responsibleTeam}）</span>}
                  {rectification.responsiblePhone && <span style={{ color: '#999', marginLeft: 4 }}>☎{rectification.responsiblePhone}</span>}
                </Descriptions.Item>
                <Descriptions.Item label="责任单位">{rectification.responsibleParty || '-'}</Descriptions.Item>
                <Descriptions.Item label="整改期限">
                  {rectification.deadline ? (
                    <span style={{ color: new Date(rectification.deadline) < new Date() &&
                      ![RectificationStatus.CLOSED, RectificationStatus.OWNER_ACCEPTED].includes(rectification.status)
                      ? 'red' : undefined }}>
                      {new Date(rectification.deadline).toLocaleDateString()}
                      {new Date(rectification.deadline) < new Date() &&
                        ![RectificationStatus.CLOSED, RectificationStatus.OWNER_ACCEPTED].includes(rectification.status) && (
                        <WarningOutlined style={{ color: 'red', marginLeft: 4 }} />
                      )}
                    </span>
                  ) : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="规范依据">{rectification.standardBasis || '-'}</Descriptions.Item>
                <Descriptions.Item label="原进度">{rectification.originalProgress}%</Descriptions.Item>
                <Descriptions.Item label="恢复后进度">
                  {rectification.progressRestored
                    ? <span style={{ color: '#52c41a' }}>{rectification.restoredProgress}%</span>
                    : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="问题描述" span={2}>{rectification.description}</Descriptions.Item>
                {rectification.acceptanceCriteria && (
                  <Descriptions.Item label="验收标准" span={2}>
                    <span style={{ color: '#52c41a' }}>{rectification.acceptanceCriteria}</span>
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Card>

            {/* 问题照片与整改照片 */}
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={12}>
                <Card title="问题照片" size="small">
                  <Image.PreviewGroup>
                    <Space wrap>
                      {rectification.issueAttachments?.length
                        ? rectification.issueAttachments.map((att, index) => (
                          <Image key={index} width={80} height={80} src={att.url} style={{ objectFit: 'cover' }} />
                        ))
                        : <span style={{ color: '#999' }}>暂无照片</span>}
                    </Space>
                  </Image.PreviewGroup>
                </Card>
              </Col>
              <Col span={12}>
                <Card title="整改后照片" size="small">
                  <Image.PreviewGroup>
                    <Space wrap>
                      {rectification.rectificationAttachments?.length
                        ? rectification.rectificationAttachments.map((att, index) => (
                          <Image key={index} width={80} height={80} src={att.url} style={{ objectFit: 'cover' }} />
                        ))
                        : <span style={{ color: '#999' }}>暂无照片</span>}
                    </Space>
                  </Image.PreviewGroup>
                </Card>
              </Col>
            </Row>

            {/* 实际成本明细 */}
            {actualCost && (
              <Card size="small" style={{ marginBottom: 16 }}>
                <Descriptions column={3} size="small">
                  <Descriptions.Item label="劳务费">¥{actualCost.laborCost?.toLocaleString() ?? '0.00'}</Descriptions.Item>
                  <Descriptions.Item label="材料费">¥{actualCost.materialCost?.toLocaleString() ?? '0.00'}</Descriptions.Item>
                  <Descriptions.Item label="机械费">¥{actualCost.equipmentCost?.toLocaleString() ?? '0.00'}</Descriptions.Item>
                  <Descriptions.Item label="分包费">¥{actualCost.subcontractCost?.toLocaleString() ?? '0.00'}</Descriptions.Item>
                  <Descriptions.Item label="协调费">¥{actualCost.coordinationCost?.toLocaleString() ?? '0.00'}</Descriptions.Item>
                  <Descriptions.Item label="误工费">¥{actualCost.delayCost?.toLocaleString() ?? '0.00'}</Descriptions.Item>
                  <Descriptions.Item label="实际总计" contentStyle={{ color: '#52c41a', fontWeight: 600 }}>
                    ¥{actualCost.total?.toLocaleString() ?? '0.00'}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            )}

            {/* 流程历史 */}
            <Card title="处理过程记录" style={{ marginBottom: 16 }}>
              <Timeline
                items={historyEvents.map((event, index) => ({
                  key: event._id || index,
                  color: event._type === 'reject' ? 'red'
                    : event._type === 'progress' ? 'blue'
                    : event._type === 'extension' ? 'orange'
                    : 'green',
                  children: (
                    <div>
                      <Space>
                        <strong>{event.userName}</strong>
                        {event._type === 'reject' && <Tag color="red">打回</Tag>}
                        {event._type === 'progress' && <Tag color="blue">进度</Tag>}
                        {event._type === 'extension' && <Tag color="orange">延期</Tag>}
                      </Space>
                      <div>{event.action}</div>
                      {event.comment && <div style={{ color: '#666', fontSize: 13 }}>{event.comment}</div>}
                      <div style={{ color: '#999', fontSize: 12 }}>
                        {new Date(event.timestamp).toLocaleString('zh-CN')}
                      </div>
                    </div>
                  ),
                }))}
              />
            </Card>

            {/* 验收意见 */}
            {(rectification.directorComment || rectification.ownerComment) && (
              <Card size="small" style={{ marginBottom: 16 }}>
                <Descriptions column={1} size="small">
                  {rectification.directorComment && (
                    <Descriptions.Item label="总监意见">
                      <span>{rectification.directorComment}</span>
                      {rectification.directorAcceptedAt && (
                        <span style={{ color: '#999', marginLeft: 8 }}>
                          {new Date(rectification.directorAcceptedAt).toLocaleString()}
                        </span>
                      )}
                    </Descriptions.Item>
                  )}
                  {rectification.ownerComment && (
                    <Descriptions.Item label="甲方意见">
                      <span>{rectification.ownerComment}</span>
                      {rectification.ownerAcceptedAt && (
                        <span style={{ color: '#999', marginLeft: 8 }}>
                          {new Date(rectification.ownerAcceptedAt).toLocaleString()}
                        </span>
                      )}
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </Card>
            )}
          </>
        )}
      </Drawer>

      <Modal
        title={
          currentAction === 'submit' ? '提交整改报告' :
          currentAction === 'director_reject' || currentAction === 'owner_reject' ? '验收打回' :
          '操作确认'
        }
        open={actionModalVisible}
        onCancel={() => {
          setActionModalVisible(false);
          setComment('');
          setRectificationAttachments([]);
        }}
        onOk={handleAction}
        okText="确认"
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          {(currentAction === 'director_reject' || currentAction === 'owner_reject') && (
            <div style={{ color: 'red', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
              <ExclamationCircleOutlined />
              <span>打回后，整改将退回"整改中"状态，可重新整改提交</span>
            </div>
          )}
          <Input.TextArea
            rows={4}
            placeholder="请输入备注信息（如打回原因）"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
          {currentAction === 'submit' && (
            <div>
              <div style={{ marginBottom: 8, color: '#666' }}>上传整改后照片（可选）：</div>
              <Upload
                fileList={rectificationAttachments}
                onChange={({ fileList }) => setRectificationAttachments(fileList)}
                beforeUpload={() => false}
                multiple
                accept="image/*"
                onRemove={file => {
                  setRectificationAttachments(prev => prev.filter(a => a.uid !== file.uid));
                  return true;
                }}
              >
                <Button icon={<UploadOutlined />}>上传整改后照片</Button>
              </Upload>
            </div>
          )}
        </Space>
      </Modal>
    </>
  );
};

export default RectificationDetailDrawer;
